'use strict';

import Axios from 'axios';
import { parseResponse, handleError } from '@/_helpers/utils.js';
import qs from 'qs';
import PROCESS_NAMES from '@/process/names.js';

export default {
  async fetchList(params) {
    let defaultParams = {
      with: ['user', 'language', 'modules', 'countries', 'user.userName'],
      fields: [
        'published',
        'company_id',
        'description',
        'is_private',
        'is_disabled_videointerview',
        'sent_responses_count',
        'new_responses_count',
        'has_admin_access'
      ]
    };

    if (params.search_term) {
      params.filter = [];
      params.filter['title'] = params.search_term;
      delete params.search_term;
    }
    params = { ...defaultParams, ...params };
    if (params.with) {
      params.with = [...params.with, ...defaultParams.with];
    }
    if (params.fields) {
      params.fields = [...params.fields, ...defaultParams.fields];
    }
    if (isNaN(params.page)) {
      params.page = 1;
    }

    return await Axios.get('/v3/vacancies', {
      params,
      paramsSerializer: params => {
        return qs.stringify(params);
      }
    })
      .then(response => {
        return {
          data: response.data._embedded.vacancies,
          meta: {
            pageNumber: response.data._page,
            totalPages: response.data._page_count,
            total: response.data._total_items,
            limit: params.limit
          }
        };
      })
      .catch(err => Promise.reject(handleError(err)));
  },

  fetchListDropdown(params) {
    return Axios.get('/v3/vacancies', {
      params,
      paramsSerializer: params => {
        return qs.stringify(params);
      }
    })
      .then(response => {
        return {
          data: response.data._embedded.vacancies,
          meta: {
            page: response.data._page,
            total: response.data._total_items,
            limit: params.limit
          }
        };
      })
      .catch(err => Promise.reject(handleError(err)));
  },

  fetchSportMasterVacancies(params, additionalFiltersQuery) {
    let url = `/1.0/spec-private/sportmaster/vacancies`;

    if (additionalFiltersQuery.length > 0) {
      url = `/1.0/spec-private/sportmaster/vacancies?${additionalFiltersQuery}`;
    }

    return Axios.get(url, {
      params,
      paramsSerializer: params => {
        return qs.stringify(params);
      }
    })
      .then(response => {
        return parseResponse(response);
      })
      .catch(err => Promise.reject(handleError(err)));
  },

  fetchOne(id) {
    return new Promise((resolve, reject) => {
      Axios.get(`/1.0/vacancies/${id}`)
        .then(response => {
          resolve(parseResponse(response).data[0]);
        })
        .catch(err => reject(handleError(err)));
    });
  },

  storeOne(model) {
    return new Promise((resolve, reject) => {
      Axios.post('/1.0/vacancies', { vacancies: [model] })
        .then(response => {
          resolve(parseResponse(response).data[0]);
        })
        .catch(err => reject(handleError(err)));
    });
  },

  updateOne(model) {
    return Axios.put('/1.0/vacancies', { vacancies: [model] })
      .then(response => parseResponse(response).data[0])
      .catch(err => {
        throw handleError(err);
      });
  },

  update(models) {
    return new Promise((resolve, reject) => {
      Axios.put('/1.0/vacancies', { vacancies: models })
        .then(response => {
          resolve(parseResponse(response).data[0]);
        })
        .catch(err => reject(handleError(err)));
    });
  },

  remove(models) {
    return new Promise((resolve, reject) => {
      Axios.delete('/1.0/vacancies', { data: { vacancies: models } })
        .then(() => {
          resolve();
        })
        .catch(err => reject(handleError(err)));
    });
  },

  copyOne(model, process = null) {
    let url = '/1.0/vacancy-copies';

    if (process === PROCESS_NAMES.KPMG) {
      url = '/1.0/spec-private/custom-process/test/vacancy/copy';
    }

    return Axios.post(url, { vacancies: [{ id: model.id }] })
      .then(response => parseResponse(response).data[0])
      .catch(err => {
        throw handleError(err);
      });
  },

  copy(modelIds) {
    return new Promise((resolve, reject) => {
      Axios.post('/1.0/vacancy-copies', { vacancies: modelIds })
        .then(response => {
          resolve(parseResponse(response).data);
        })
        .catch(err => reject(handleError(err)));
    });
  },

  fetchAvailableCompetencies(id) {
    return new Promise((resolve, reject) => {
      Axios.get(`/1.0/vacancy/${id}/competences/settings`)
        .then(response => {
          resolve(parseResponse(response).data[0]);
        })
        .catch(err => reject(handleError(err)));
    });
  },

  fetchCompetencies(id) {
    return new Promise((resolve, reject) => {
      Axios.get(`/1.0/vacancy/${id}/competences`)
        .then(response => {
          resolve(parseResponse(response).data);
        })
        .catch(err => reject(handleError(err)));
    });
  },

  updateCompetence(vacancyId, competences) {
    return new Promise((resolve, reject) => {
      Axios.put(`/1.0/vacancy/${vacancyId}/competences`, { competences })
        .then(response => {
          resolve(parseResponse(response).data);
        })
        .catch(err => reject(handleError(err)));
    });
  },

  updateCompetencies(vacancyId, competences) {
    return Axios.put(`/1.0/vacancy/${vacancyId}/competences`, { competences })
      .then(response => parseResponse(response).data)
      .catch(err => {
        throw handleError(err);
      });
  },

  createCompetencies(vacancyId, competences) {
    return Axios.post(`/1.0/vacancy/${vacancyId}/competences`, { competences })
      .then(response => parseResponse(response).data)
      .catch(err => {
        throw handleError(err);
      });
  },

  deleteCompetencies(vacancyId, competences) {
    return Axios.delete(`/1.0/vacancy/${vacancyId}/competences`, { data: { competences } })
      .then(response => parseResponse(response).data)
      .catch(err => {
        throw handleError(err);
      });
  },

  fetchPermissions(id) {
    return new Promise((resolve, reject) => {
      Axios.get(`/1.0/vacancies/${id}/permissions`)
        .then(response => {
          resolve(parseResponse(response).data);
        })
        .catch(err => reject(handleError(err, false)));
    });
  },

  updatePermissions({ id, permissions }) {
    return Axios.put(`/1.0/vacancies/${id}/permissions`, { permissions })
      .then(response => parseResponse(response).data)
      .catch(err => {
        throw handleError(err);
      });
  },

  deletePermissions({ id, permissions }) {
    return Axios.delete(`/1.0/vacancies/${id}/permissions`, { data: { permissions } })
      .then(response => parseResponse(response).data)
      .catch(err => {
        throw handleError(err);
      });
  },

  createResponseByPublicVacancy({ vacancyToken, candidate }) {
    return new Promise((resolve, reject) => {
      Axios.post(`/1.0/public-vacancies/${vacancyToken}/responses`, { responses: [{ candidate }] })
        .then(response => resolve(parseResponse(response).data[0]))
        .catch(err => reject(handleError(err)));
    });
  },

  fetchCompanyInfo({ vacancyToken }) {
    return new Promise((resolve, reject) => {
      return Axios.get(`/1.0/public-vacancies/${vacancyToken}`)
        .then(response => resolve(parseResponse(response).data[0]))
        .catch(err => reject(handleError(err)));
    });
  },

  publicVacancySaveCandidateResume({ vacancyToken, responseToken, resumeFile }) {
    const data = new FormData();

    data.append('resume', resumeFile);

    return new Promise((resolve, reject) => {
      Axios.post(`/1.0/public-vacancies/${vacancyToken}/responses/${responseToken}/resume`, data)
        .then(response => resolve(parseResponse(response).data[0]))
        .catch(err => reject(handleError(err)));
    });
  },

  async getTempatesDefault() {
    return await Axios.get(`/1.0/response-status-email-template-defaults`)
      .then(response => {
        return parseResponse(response).data;
      })
      .catch(err => {
        throw new Error(handleError(err));
      });
  },

  createTemplateDefault(models) {
    return Axios.post(`/1.0/response-status-email-template-defaults`, { templates: [models] })
      .then(response => parseResponse(response).data)
      .catch(err => {
        throw new Error(handleError(err));
      });
  },

  updateTemplateDefault(models) {
    return Axios.put('/1.0/response-status-email-template-defaults', { templates: [models] })
      .then(response => parseResponse(response).data)
      .catch(err => {
        throw new Error(handleError(err));
      });
  },

  async getEmailTemplatesAll(id) {
    return await Axios.get(`/1.0/vacancies/${id}/response-status-email-templates`)
      .then(response => {
        return parseResponse(response).data;
      })
      .catch(err => {
        throw new Error(handleError(err));
      });
  },

  getSurveyScreeningTemplate(id) {
    return new Promise((resolve, reject) => {
      Axios.get(`/v2/vacancies/${id}/survey-screening/email`)
        .then(response => {
          resolve(response.data);
        })
        .catch(err => reject(handleError(err)));
    });
  },

  getTestDropoutTemplate(id) {
    return new Promise((resolve, reject) => {
      Axios.get(`/v2/vacancies/${id}/test-dropout/email`)
        .then(response => {
          resolve(response.data);
        })
        .catch(err => reject(handleError(err)));
    });
  },

  createTemplateCustom(id, model) {
    return new Promise((resolve, reject) => {
      Axios.post(`/1.0/vacancies/${id}/response-status-email-templates`, { templates: [model] })
        .then(response => {
          resolve(parseResponse(response).data);
        })
        .catch(err => reject(handleError(err)));
    });
  },

  saveVacancyLogo(vacancyId, logo) {
    let data = new FormData();
    data.append('logo', logo, new Date().toJSON());

    return Axios.post(`1.0/vacancies/${vacancyId}/logo`, data)
      .then(response => parseResponse(response).data[0])
      .catch(err => {
        throw handleError(err);
      });
  },

  removeVacancyLogo(vacancyId) {
    return Axios.delete(`1.0/vacancies/${vacancyId}/logo`)
      .then(response => parseResponse(response).data[0])
      .catch(err => {
        throw handleError(err);
      });
  },

  recoverVacancyTemplate(vacancyId, templateIds) {
    return new Promise((resolve, reject) => {
      Axios.post(`/1.0/vacancies/${vacancyId}/response-status-email-templates/recovery`, {
        templates_ids: templateIds
      })
        .then(response => {
          resolve(parseResponse(response).data);
        })
        .catch(err => reject(handleError(err)));
    });
  },

  updateTemplateCustom(id, model) {
    return new Promise((resolve, reject) => {
      Axios.put(`/1.0/vacancies/${id}/response-status-email-templates`, { templates: [model] })
        .then(response => {
          resolve(parseResponse(response).data);
        })
        .catch(err => reject(handleError(err)));
    });
  },

  updateEmailSurveyTemplate(id, model) {
    return new Promise((resolve, reject) => {
      Axios.put(`/v2/vacancies/${id}/test-dropout/email `, model)
        .then(response => {
          resolve(response.data);
        })
        .catch(err => reject(handleError(err)));
    });
  },

  updateEmailScreeningTemplate(id, model) {
    return new Promise((resolve, reject) => {
      Axios.put(`/v2/vacancies/${id}/survey-screening/email `, model)
        .then(response => {
          resolve(response.data);
        })
        .catch(err => reject(handleError(err)));
    });
  },

  deleteTemplateCustom(id, response) {
    return new Promise((resolve, reject) => {
      Axios.delete(`/1.0/vacancies/${id}/response-status-email-templates/${response}`)
        .then(response => {
          resolve(parseResponse(response).data);
        })
        .catch(err => reject(handleError(err)));
    });
  }
};
